import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowDownLeft, ArrowDownRight, CheckCircle2, ChevronRight, User, Package, ClipboardList } from "lucide-react";

const FIELD = "mt-1.5 w-full h-11 px-4 rounded-lg border border-slate-200 outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 text-sm bg-white";
const TEXTAREA = "mt-1.5 w-full px-4 py-3 rounded-lg border border-slate-200 outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100 text-sm resize-none bg-white";
const LABEL = "text-sm font-semibold text-slate-700";

const STEPS = [
    { label: "Sponsor Details", icon: User },
    { label: "Choose Plan", icon: Package },
    { label: "Personal Info", icon: ClipboardList },
];

function StepIndicator({ current }) {
    return (
        <div className="flex items-center gap-0 mb-8">
            {STEPS.map((s, i) => {
                const done = i < current;
                const active = i === current;
                const Icon = s.icon;
                return (
                    <div key={i} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors
                                ${done ? "bg-[#2563EB] border-[#2563EB]" : active ? "border-[#2563EB] bg-white" : "border-slate-200 bg-white"}`}>
                                {done
                                    ? <CheckCircle2 className="w-5 h-5 text-white" />
                                    : <Icon className={`w-4 h-4 ${active ? "text-[#2563EB]" : "text-slate-400"}`} />}
                            </div>
                            <span className={`text-[10px] font-semibold whitespace-nowrap ${active ? "text-[#2563EB]" : done ? "text-slate-500" : "text-slate-400"}`}>
                                {s.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-1 mb-4 ${done ? "bg-[#2563EB]" : "bg-slate-200"}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Step 1 ────────────────────────────────────────────────────────────────────
function Step1({ defaultCode, onConfirm }) {
    const [code, setCode] = useState(defaultCode || "");
    const [side, setSide] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const verify = async () => {
        if (!code.trim()) { setError("Please enter a Sponsor ID"); return; }
        if (!side) { setError("Please choose a side"); return; }
        setError("");
        setLoading(true);
        try {
            const { data } = await api.get("/auth/sponsor-lookup", { params: { code: code.trim().toUpperCase(), side } });
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Sponsor ID not found");
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSide = (s) => {
        setSide(s);
        setResult(null);
        setError("");
    };

    return (
        <div className="space-y-5">
            <div>
                <label className={LABEL}>Sponsor's ID <span className="text-red-500">*</span></label>
                <input
                    className={FIELD}
                    placeholder="Enter referral / Member ID"
                    value={code}
                    onChange={e => { setCode(e.target.value); setResult(null); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && verify()}
                />
            </div>

            <div>
                <div className={LABEL + " mb-1.5"}>Place On Side <span className="text-red-500">*</span></div>
                <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => handleSide("L")}
                        className={`h-11 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors
                            ${side === "L" ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-700 border-slate-200 hover:border-[#2563EB]/50"}`}>
                        <ArrowDownLeft className="w-4 h-4" /> Left
                    </button>
                    <button type="button" onClick={() => handleSide("R")}
                        className={`h-11 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors
                            ${side === "R" ? "bg-[#1B2B6B] text-white border-[#1B2B6B]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1B2B6B]/50"}`}>
                        <ArrowDownRight className="w-4 h-4" /> Right
                    </button>
                </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            {!result && (
                <button type="button" onClick={verify} disabled={loading}
                    className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1B2B6B] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                    Verify & Continue
                </button>
            )}

            {result && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 divide-y divide-blue-100 overflow-hidden">
                    {/* Sponsor row */}
                    <div className="px-4 py-3 flex items-center justify-between">
                        <div>
                            <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Sponsor's ID</div>
                            <div className="font-mono font-bold text-[#2563EB]">{result.sponsor_code}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Sponsor's Name</div>
                            <div className="font-semibold text-slate-800">{result.sponsor_name}</div>
                        </div>
                    </div>

                    {/* Placement row */}
                    {result.same_as_sponsor ? (
                        <div className="px-4 py-3 flex items-center gap-2 bg-green-50">
                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                            <p className="text-sm text-green-700">
                                <span className="font-semibold">{side === "L" ? "Left" : "Right"} slot is available</span> — you will be placed directly under this sponsor.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Adjusted To ID</div>
                                    <div className="font-mono font-bold text-slate-700">{result.adjusted_to_code}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Adjusted To Name</div>
                                    <div className="font-semibold text-slate-800">{result.adjusted_to_name}</div>
                                </div>
                            </div>
                            <div className="px-4 py-2 bg-amber-50">
                                <p className="text-[11px] text-amber-700">
                                    Spillover applied — {side === "L" ? "Left" : "Right"} slot under sponsor is taken.
                                    You will be placed under <span className="font-semibold">{result.adjusted_to_name}</span>.
                                </p>
                            </div>
                        </>
                    )}

                    <div className="px-4 py-3">
                        <button type="button" onClick={() => onConfirm({ referral_code: result.sponsor_code, side })}
                            className="w-full h-10 rounded-lg bg-[#2563EB] hover:bg-[#1B2B6B] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                            Confirm to Continue <ChevronRight className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => setResult(null)}
                            className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600 text-center">
                            Change sponsor or side
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Step 2 ────────────────────────────────────────────────────────────────────
function Step2({ onSelect, onBack }) {
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/products").then(r => setProducts(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-4">
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" />
                </div>
            )}

            {!loading && products.map(p => (
                <button key={p.id} type="button" onClick={() => setSelected(p)}
                    className={`w-full text-left rounded-xl border-2 p-4 transition-colors
                        ${selected?.id === p.id ? "border-[#2563EB] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-semibold text-slate-900">{p.name}</div>
                            {p.description && <div className="mt-0.5 text-sm text-slate-500">{p.description}</div>}
                        </div>
                        <div className="shrink-0 text-right">
                            <div className="text-lg font-black text-[#2563EB]">₹{Number(p.price).toLocaleString("en-IN")}</div>
                            {selected?.id === p.id && <CheckCircle2 className="w-5 h-5 text-[#2563EB] mt-1 ml-auto" />}
                        </div>
                    </div>
                </button>
            ))}

            {!loading && products.length === 0 && (
                <p className="text-center text-slate-500 py-8">No plans available at the moment.</p>
            )}

            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                    className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Back
                </button>
                <button type="button" disabled={!selected} onClick={() => onSelect(selected)}
                    className="flex-[2] h-11 rounded-lg bg-[#2563EB] hover:bg-[#1B2B6B] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40 transition-colors">
                    Continue <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

// ── Step 3 ────────────────────────────────────────────────────────────────────
function Step3({ sponsorData, product, onBack }) {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", password: "",
        occupation: "", guardian_name: "", address: "",
    });
    const [loading, setLoading] = useState(false);
    const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                referral_code: sponsorData.referral_code,
                side: sponsorData.side,
                product_id: product.id,
            };
            if (!payload.email) delete payload.email;
            if (!payload.phone) delete payload.phone;
            if (!payload.occupation) delete payload.occupation;
            if (!payload.guardian_name) delete payload.guardian_name;
            if (!payload.address) delete payload.address;
            await register(payload);
            toast.success("Account created!");
            nav("/app/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {/* Summary strip */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex gap-6">
                <div>
                    <span className="text-slate-400 text-xs">Sponsor</span>
                    <div className="font-semibold text-slate-700">{sponsorData.referral_code} · {sponsorData.side === "L" ? "Left" : "Right"}</div>
                </div>
                <div className="border-l border-slate-200 pl-6">
                    <span className="text-slate-400 text-xs">Plan</span>
                    <div className="font-semibold text-slate-700">{product.name} — ₹{Number(product.price).toLocaleString("en-IN")}</div>
                </div>
            </div>

            <label className="block">
                <span className={LABEL}>Full Name <span className="text-red-500">*</span></span>
                <input required value={form.full_name} onChange={set("full_name")} className={FIELD} placeholder="As per Aadhaar / PAN" />
            </label>
            <label className="block">
                <span className={LABEL}>Mobile Number</span>
                <input type="tel" maxLength={10} value={form.phone} onChange={set("phone")} className={FIELD} placeholder="10-digit mobile number" />
            </label>
            <label className="block">
                <span className={LABEL}>Email ID <span className="text-slate-400 font-normal text-xs">(optional)</span></span>
                <input type="email" value={form.email} onChange={set("email")} className={FIELD} placeholder="you@example.com" />
                <span className="text-[11px] text-slate-400 mt-1 block">Leave blank — you can log in with your Member ID</span>
            </label>
            <label className="block">
                <span className={LABEL}>Occupation</span>
                <input value={form.occupation} onChange={set("occupation")} className={FIELD} placeholder="e.g. Business, Farmer, Student" />
            </label>
            <label className="block">
                <span className={LABEL}>Father / Spouse Name</span>
                <input value={form.guardian_name} onChange={set("guardian_name")} className={FIELD} placeholder="Father's or Spouse's full name" />
            </label>
            <label className="block">
                <span className={LABEL}>Address</span>
                <textarea rows={3} value={form.address} onChange={set("address")} className={TEXTAREA} placeholder="Full residential address" />
            </label>
            <label className="block">
                <span className={LABEL}>Password <span className="text-red-500">*</span></span>
                <input required type="password" minLength={6} value={form.password} onChange={set("password")} className={FIELD} placeholder="Minimum 6 characters" />
            </label>

            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                    className="flex-1 h-11 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors">
                    Back
                </button>
                <button type="submit" disabled={loading}
                    className="flex-[2] h-11 rounded-lg bg-[#2563EB] hover:bg-[#1B2B6B] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Create My Account
                </button>
            </div>
        </form>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Register() {
    const [params] = useSearchParams();
    const [step, setStep] = useState(0);
    const [sponsorData, setSponsorData] = useState(null);   // { referral_code, side }
    const [product, setProduct] = useState(null);

    const defaultCode = params.get("ref") || "";

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            {/* Left panel */}
            <div className="hidden md:flex flex-col justify-between p-12 bg-gradient-to-br from-[#1A3C5E] to-[#2563EB] text-white">
                <Link to="/" className="flex items-center gap-2 font-display font-black text-xl">
                    <span className="w-8 h-8 rounded-lg bg-white/20 grid place-items-center font-black">B</span>
                    Zenwyxon
                </Link>
                <div>
                    <h1 className="font-display font-black text-5xl tracking-tighter">Join Zenwyxon today.</h1>
                    <p className="mt-6 text-white/80 text-lg max-w-md">
                        Create your account, get your unique Member ID, and begin building your network.
                    </p>
                    <div className="mt-8 space-y-2 text-sm text-blue-100">
                        <div>✓ Unique Member ID assigned instantly</div>
                        <div>✓ Up to 3 Member IDs per mobile number</div>
                        <div>✓ Earn ₹600 direct bonus per referral activation</div>
                        <div>✓ Pairing bonus — up to 10 pairs/day</div>
                    </div>
                </div>
                <div className="text-sm text-white/50">Zenwyxon — Transparent Binary Network Platform</div>
            </div>

            {/* Right panel */}
            <div className="flex items-start justify-center p-8 md:p-12 bg-white overflow-y-auto">
                <div className="w-full max-w-md">
                    <div className="overline mb-1">Create account</div>
                    <h2 className="font-display font-black text-3xl tracking-tighter text-slate-900 mb-6">Join Zenwyxon</h2>

                    <StepIndicator current={step} />

                    {step === 0 && (
                        <Step1
                            defaultCode={defaultCode}
                            onConfirm={data => { setSponsorData(data); setStep(1); }}
                        />
                    )}
                    {step === 1 && (
                        <Step2
                            onBack={() => setStep(0)}
                            onSelect={p => { setProduct(p); setStep(2); }}
                        />
                    )}
                    {step === 2 && (
                        <Step3
                            sponsorData={sponsorData}
                            product={product}
                            onBack={() => setStep(1)}
                        />
                    )}

                    <p className="mt-6 text-sm text-slate-600 text-center">
                        Already registered?{" "}
                        <Link to="/login" className="font-semibold text-[#2563EB] hover:underline">Log in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
